// Shared primitives for the mortgage scenario calculator. Styled to match the
// Elin's Consultancy look (deep green / cream / gold).

export const Panel = ({ title, subtitle, children, testid }) => (
  <section
    data-testid={testid}
    className="bg-white border border-forest/10 p-6 sm:p-8 space-y-5"
  >
    {title && (
      <div>
        <h2 className="font-serif text-2xl sm:text-[26px] text-forest tracking-tight">
          {title}
        </h2>
        {subtitle && (
          <p className="text-[13px] text-sage mt-1.5 leading-relaxed">{subtitle}</p>
        )}
      </div>
    )}
    {children}
  </section>
);

export const Field = ({ label, help, children, wide }) => (
  <div className={wide ? "sm:col-span-2" : ""}>
    <label className="block text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70 mb-2">
      {label}
    </label>
    {children}
    {help && (
      <p className="text-[11.5px] text-sage mt-2 leading-relaxed">{help}</p>
    )}
  </div>
);

export const Input = ({ testid, ...rest }) => (
  <input
    data-testid={testid}
    {...rest}
    className={`w-full bg-white border border-forest/15 px-3.5 py-2.5 text-[14px] text-ink focus:border-gold focus:outline-none transition-colors ${
      rest.className || ""
    }`}
  />
);

export const Select = ({ testid, children, ...rest }) => (
  <select
    data-testid={testid}
    {...rest}
    className={`w-full bg-white border border-forest/15 px-3.5 py-2.5 text-[14px] text-ink focus:border-gold focus:outline-none transition-colors ${
      rest.className || ""
    }`}
  >
    {children}
  </select>
);

export const Checkbox = ({ label, checked, onChange, testid }) => (
  <label
    data-testid={testid ? `${testid}-row` : undefined}
    className="flex items-center gap-3 cursor-pointer select-none"
  >
    <span
      className={`inline-flex w-5 h-5 border items-center justify-center shrink-0 transition-colors ${
        checked
          ? "bg-forest border-forest text-cream"
          : "bg-white border-forest/40"
      }`}
    >
      {checked && (
        <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
          <path d="M4 10l4 4 8-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="sr-only"
      data-testid={testid}
    />
    <span className="text-[13.5px] text-ink font-semibold">{label}</span>
  </label>
);

export const Card = ({ eyebrow, value, note, featured, testid }) => (
  <div
    data-testid={testid}
    className={`p-5 sm:p-6 border ${
      featured
        ? "bg-forest text-cream border-forest"
        : "bg-cream-3 text-forest border-forest/10"
    }`}
  >
    <p
      className={`text-[11px] tracking-[0.16em] uppercase font-bold ${
        featured ? "text-cream/80" : "text-forest/70"
      }`}
    >
      {eyebrow}
    </p>
    <p
      className={`font-serif text-[28px] sm:text-[32px] leading-tight mt-1 ${
        featured ? "text-cream" : "text-forest"
      }`}
    >
      {value}
    </p>
    {note && (
      <p
        className={`text-[12px] mt-1.5 leading-snug ${
          featured ? "text-cream/75" : "text-sage"
        }`}
      >
        {note}
      </p>
    )}
  </div>
);

export const Row = ({ label, value, testid }) => (
  <div
    data-testid={testid}
    className="flex items-center justify-between gap-3 py-2 border-b border-forest/5 last:border-b-0 text-[13.5px]"
  >
    <span className="text-forest/80">{label}</span>
    <strong className="text-forest font-semibold tabular-nums">{value}</strong>
  </div>
);
